import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Axios from "axios";
import DOMPurify from "dompurify";
import toast from "react-hot-toast";
import { buildUserState } from "../../utilities/tokenUtils";
import UserContext from "../../context/UserContext";

const schema = yup.object().shape({
    totpCode: yup
        .string()
        .required("Código MFA es requerido")
        .matches(/^[0-9]{6}$/, "Debe ser exactamente 6 dígitos")
        .min(6, "Debe tener exactamente 6 dígitos")
        .max(6, "Debe tener exactamente 6 dígitos"),
});

export default function MFAVerify() {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [attempts, setAttempts] = useState(0);
    const MAX_ATTEMPTS = 3;

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm({
        resolver: yupResolver(schema),
    });

    const { setUserData } = useContext(UserContext);
    const navigate = useNavigate();

    useEffect(() => {
        document.getElementById("totpCode")?.focus();
        if (!sessionStorage.getItem("partial-token")) {
            navigate("/admin/login");
        }
    }, [navigate]);

    const onSubmit = async (data) => {
        setIsSubmitting(true);
        try {
            const partialToken = sessionStorage.getItem("partial-token");

            const sanitizedData = {
                codeTOTP: DOMPurify.sanitize(data.totpCode),
            };

            const res = await Axios.post(
                `${process.env.REACT_APP_BACK_DOMAIN}/users/validate-mfa`,
                sanitizedData,
                {
                    headers: {
                        "x-partial-token": partialToken,
                    },
                }
            );

            sessionStorage.removeItem("partial-token");

            setUserData(buildUserState(res.data.token));

            localStorage.setItem("auth-token", res.data.token);
            toast.success("Autenticación MFA exitosa");

            navigate("/");
        } catch (err) {
            if (err.response?.status === 403) {
                sessionStorage.removeItem("partial-token");
                toast.error("Sesión expirada. Inicie sesión nuevamente.");
                navigate("/admin/login");
            } else if (err.response?.status === 400) {
                setAttempts(prev => prev + 1);
                reset({ totpCode: "" });
                if (attempts >= MAX_ATTEMPTS - 1) {
                    toast.error("Demasiados intentos fallidos. Redirigiendo al login.");
                    navigate("/admin/login");
                }
            } else if (err.response?.data?.msg) {
                toast.error(err.response.data.msg);
            } else {
                toast.error("Error al verificar código MFA");
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="page">
            <div className="container">
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <button
                        className="Button"
                        onClick={() => navigate("/admin/login")}
                        disabled={isSubmitting}
                    >
                        Regresar al Login
                    </button>
                </div>

                <h2>Ingresa tu código MFA</h2>

                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="Form-Field">
                        <label htmlFor="totpCode" className="Form-Label">
                            Código de autenticación:
                        </label>
                        <input
                            id="totpCode"
                            type="text"
                            className="Form-Input"
                            placeholder="Código de 6 dígitos"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            maxLength={6}
                            {...register("totpCode")}
                        />
                        {errors.totpCode && (
                            <span style={{ color: "red" }}>
                                {errors.totpCode.message}
                            </span>
                        )}
                    </div>

                    <div style={{ marginTop: "1rem" }}>
                        <button
                            type="submit"
                            className="Button Button_primary"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? "Verificando..." : "Verificar Código"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}